import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import ColumnGroupingTable from "../../../table/ColumnGroupingTable.component";
import { Typography } from "@mui/material";
import { useHedgeRatioContext } from "../../../../context/HedgeRatioContext"; // 💡 Added import
import { applyFigureFormatting } from "../../../../utils/utils"; // 💡 Added import

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const CreditFacilityTable = ({
  setCreditFacilityTableDatas,
  creditFacilityResponse,
} = {}) => {
  const { selectedFigureDivisor, selectedDecimalsIn } = useHedgeRatioContext();
  const columns = [
    {
      id: "creditFacility",
      label: "Credit Facility",
      minWidth: 340,
    },
    {
      id: "facility",
      label: "Facility",
      minWidth: 140,
      align: "right",
    },
    {
      id: "utilized",
      label: "Utilized",
      minWidth: 140,
      align: "right",
    },
    {
      id: "unUtilized",
      label: "Unutilized",
      minWidth: 140,
      align: "right",
    },
  ];

  const { commercialPaperRows, totalRow, totalUnUtilizedRow } = useMemo(() => {
    const commPaperRowsRaw =
      creditFacilityResponse?.map(({ creditFacility, facility, utilized }) => ({
        creditFacility,
        facility: Number(facility) || 0,
        utilized: Number(utilized) || 0,
        unUtilized: (Number(facility) || 0) - (Number(utilized) || 0),
        minWidth: 120,
        align: "left",
      })) || [];

    let calculatedTotalRowRaw = {
      creditFacility: "Total Facilities",
      facility: 0,
      utilized: 0,
      unUtilized: 0,
    };

    if (commPaperRowsRaw.length > 0) {
      calculatedTotalRowRaw = commPaperRowsRaw.reduce((acc, row) => {
        acc.facility += row.facility;
        acc.utilized += row.utilized;
        acc.unUtilized += row.unUtilized;
        return acc;
      }, calculatedTotalRowRaw);
    }

    const formattedCommPaperRows = commPaperRowsRaw.map((row) => ({
      ...row,
      facility: applyFigureFormatting(
        row.facility,
        selectedFigureDivisor,
        selectedDecimalsIn
      ),
      utilized: applyFigureFormatting(
        row.utilized,
        selectedFigureDivisor,
        selectedDecimalsIn
      ),
      unUtilized: applyFigureFormatting(
        row.unUtilized,
        selectedFigureDivisor,
        selectedDecimalsIn
      ),
    }));

    const formattedTotalRow = {
      ...calculatedTotalRowRaw,
      creditFacility: "Total Facilities",
      facility: applyFigureFormatting(
        calculatedTotalRowRaw.facility,
        selectedFigureDivisor,
        selectedDecimalsIn
      ),
      utilized: applyFigureFormatting(
        calculatedTotalRowRaw.utilized,
        selectedFigureDivisor,
        selectedDecimalsIn
      ),
      unUtilized: applyFigureFormatting(
        calculatedTotalRowRaw.unUtilized,
        selectedFigureDivisor,
        selectedDecimalsIn
      ),
      minWidth: 120,
      align: "left",
    };

    const formattedTotalUnUtilizedRow = {
      creditFacility: "Unutilized Credit Facility",
      facility: "",
      utilized: "",
      unUtilized: applyFigureFormatting(
        calculatedTotalRowRaw.unUtilized,
        selectedFigureDivisor,
        selectedDecimalsIn
      ),
      minWidth: 120,
      align: "left",
      cellStyles: {
        fontWeight: "bold",
        backgroundColor: "#F5F5F5",
        borderTop: "1px solid #000000",
        borderBottom: "1px solid #000000",
      },
    };

    return {
      commercialPaperRows: formattedCommPaperRows,
      totalRow: formattedTotalRow,
      totalUnUtilizedRow: formattedTotalUnUtilizedRow,
    };
  }, [creditFacilityResponse, selectedFigureDivisor, selectedDecimalsIn]);

  const datas = useMemo(() => {
    return commercialPaperRows.length > 0
      ? [...commercialPaperRows, totalRow, totalUnUtilizedRow]
      : [];
  }, [commercialPaperRows, totalRow, totalUnUtilizedRow]);

  useEffect(() => {
    if (datas && datas?.length) {
      setCreditFacilityTableDatas(datas);
    }
  }, [datas, setCreditFacilityTableDatas]);

  return (
    <Grid container spacing={2} sx={{ marginBottom: 0 }}>
      <Grid item xs={12} md={12} lg={12}>
        {!creditFacilityResponse ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              There was an error in fetching the data. Please try again later.
            </Typography>
          </Item>
        ) : !creditFacilityResponse?.length ? (
          <Item>
            <Typography
              variant="p"
              component="p"
              sx={{
                color: "#adadad",
                minHeight: "250px",
              }}
            >
              No data available for the selected filters.
            </Typography>
          </Item>
        ) : (
          <ColumnGroupingTable
            columns={columns}
            datas={datas}
            loading={false}
            disablePagination={true}
            applyThousandsSeparator={true}
            tableHeight={280}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default CreditFacilityTable;
